import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ViewNewDirectOrder.css";
import { Input, Modal, Spin, Tooltip, message } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import APIService from "../../Service/APIService";
import PublicAPIService from "../../Service/PublicAPIService";
import Text from "../../common/typography/text";
import Tags from "../../common/tags";
import Button from "../../common/button/button";
import ProfileProviderIcon from "../../icons/provider-profile-icon";
import TickIcon from "../../icons/tick-icon";
import { useAuth } from "../AuthProvider";
import { captureOpenOrderAccessToken } from "../OpenOrder/openOrderAccessToken";
import { hasPermission } from "../../Config/permissions.js";

const { TextArea } = Input;

const formatCurrency = (value) =>
  Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value, format = "MM/DD/YYYY") => {
  if (!value) {
    return "--";
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format(format) : "--";
};

const formatQuantity = (value) => String(Math.max(Number(value ?? 0), 0)).padStart(2, "0");

const buildUserFullName = (user) =>
  [user?.FirstName, user?.LastName].filter(Boolean).join(" ").trim();

const getSafeTrimmedValue = (value) => String(value ?? "").trim();

const hasTrackingNumber = (trackingNumber) => Boolean(getSafeTrimmedValue(trackingNumber));

const buildTrackingSearchUrl = (trackingNumber) => {
  const safeTrackingNumber = getSafeTrimmedValue(trackingNumber);

  if (!safeTrackingNumber) {
    return null;
  }

  return `https://www.google.com/search?q=${encodeURIComponent(safeTrackingNumber)}`;
};

const normalizeDirectOrderPaymentStatus = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const PREPAID_DIRECT_ORDER_SUCCESS_STATUSES = new Set([
  "approved",
  "completed",
  "payment successful",
  "paid",
  "success",
  "succeeded",
]);

const PREPAID_DIRECT_ORDER_LOCKOUT_STATUSES = new Set([
  "pending",
  "pending payment",
  "payment pending",
  "payment incomplete",
  "payment failed",
  "checkout expired",
  "expired",
  "failed",
]);

const isPrepaidDirectOrderAdminActionLocked = ({ order, paymentStatus }) => {
  if (order?.isPrepaidOrder !== true) {
    return false;
  }

  const orderStatus = normalizeDirectOrderPaymentStatus(order?.status);
  if (PREPAID_DIRECT_ORDER_SUCCESS_STATUSES.has(orderStatus)) {
    return false;
  }

  const statuses = [
    orderStatus,
    paymentStatus?.resolvedState,
    paymentStatus?.statusLabel,
    paymentStatus?.label,
  ]
    .map(normalizeDirectOrderPaymentStatus)
    .filter(Boolean);

  return (
    statuses.some((status) => PREPAID_DIRECT_ORDER_LOCKOUT_STATUSES.has(status)) ||
    !PREPAID_DIRECT_ORDER_SUCCESS_STATUSES.has(orderStatus)
  );
};

const openCheckoutInSameTab = (checkoutUrl) => {
  const openedWindow = window.open(checkoutUrl, "_self");

  if (!openedWindow) {
    window.location.assign(checkoutUrl);
  }
};

const DIRECT_ORDER_CHECKOUT_LAUNCH_KEY_PREFIX = "direct-order-checkout-launch";

const getCheckoutLaunchKey = (orderId) =>
  `${DIRECT_ORDER_CHECKOUT_LAUNCH_KEY_PREFIX}:${orderId}`;

const consumeCheckoutLaunchIntent = (orderId) => {
  if (!orderId) {
    return null;
  }

  try {
    const storageKey = getCheckoutLaunchKey(orderId);
    const storedIntent = window.sessionStorage.getItem(storageKey);
    if (!storedIntent) {
      return null;
    }

    window.sessionStorage.removeItem(storageKey);

    const parsedIntent = JSON.parse(storedIntent);
    const checkoutUrl =
      typeof parsedIntent?.checkoutUrl === "string"
        ? parsedIntent.checkoutUrl.trim()
        : "";

    return checkoutUrl || null;
  } catch (error) {
    console.error("[ViewNewDirectOrder] Failed to consume checkout launch intent", error);
    return null;
  }
};

const getHeaderTagConfig = (status, fallbackTagText) => {
  const normalizedStatus = String(status ?? "").trim().toLowerCase();
  if (normalizedStatus === "in transit") return { text: "In Transit", tagBackground: "blueBackground", textColor: "primaryColor" };
  if (normalizedStatus === "delayed") return { text: "Delayed", tagBackground: "warningColorLight", textColor: "warningColor" };

  if (
    normalizedStatus === "payment pending" ||
    normalizedStatus === "payment failed" ||
    normalizedStatus === "pending"
  ) {
    return {
      text: fallbackTagText || "Pending",
      tagBackground: "blueBackground",
      textColor: "primaryColor",
    };
  }

  if (
    normalizedStatus === "approved" ||
    normalizedStatus === "shipped" ||
    normalizedStatus === "in transit" ||
    normalizedStatus === "delayed" ||
    normalizedStatus === "completed"
  ) {
    return {
      text: String(status ?? "").trim() || "Approved",
      tagBackground: "successColorLight",
      textColor: "successColorDark",
    };
  }


  if (normalizedStatus === "rejected") {
    return {
      text: "Rejected",
      tagBackground: "failureColorLight",
      textColor: "failureColorDark",
    };
  }

  return {
    text: fallbackTagText || String(status ?? "").trim() || "Pending",
    tagBackground: "blueBackground",
    textColor: "primaryColor",
  };
};

const deriveStatusCard = ({ order, paymentStatus, summary }) => {
  const normalizedStatus = String(order?.status ?? "").trim().toLowerCase();
  const displayStatus = String(order?.status ?? "").trim() || "Pending";
  const invoiceId = summary?.invoiceId;
  const invoiceCompleted = summary?.invoiceCompleted === true;
  const prepaidOrder = order?.isPrepaidOrder === true;
  const trackingAvailable = hasTrackingNumber(order?.trackingNumber);

  if (
    (normalizedStatus === "approved" ||
      normalizedStatus === "shipped" ||
    normalizedStatus === "in transit" ||
    normalizedStatus === "delayed" ||
      normalizedStatus === "completed") &&
    !prepaidOrder
  ) {
    return {
      label: displayStatus,
      tone: "success",
      actions: [
        {
          key: "view-invoice",
          label: "View Invoice",
          variant: "primary",
          disabled: !invoiceId || !invoiceCompleted,
        },
        {
          key: "track-order",
          label: "Track Order",
          variant: "success",
          disabled: !trackingAvailable,
        },
      ],
    };
  }

  if (paymentStatus?.label && paymentStatus?.tone) {
    const actions = [];

    if (paymentStatus?.canContinuePayment || paymentStatus?.canRetryPayment) {
      actions.push({
        key: paymentStatus?.canContinuePayment ? "continue-payment" : "retry-payment",
        label: paymentStatus?.paymentActionLabel || "Retry Payment",
        variant: "primary",
        disabled: false,
      });
    }

    if (paymentStatus?.tone === "success") {
      actions.push(
        {
          key: "view-invoice",
          label: "View Invoice",
          variant: "primary",
          disabled: !paymentStatus?.canViewInvoice,
        },
        {
          key: "track-order",
          label: "Track Order",
          variant: "success",
          disabled: !trackingAvailable,
        },
        {
          key: "download-receipt",
          label: "Download Receipt",
          variant: "success",
          disabled: !paymentStatus?.canDownloadReceipt,
        },
      );
    }

    return {
      ...paymentStatus,
      actions,
      invoicePath:
        paymentStatus?.invoicePath ||
        (invoiceId ? `/InvoiceHome/${invoiceId}` : null),
      canViewInvoice:
        paymentStatus?.canViewInvoice ||
        (paymentStatus?.tone === "success" && Boolean(invoiceId)),
    };
  }

  if (normalizedStatus === "payment pending") {
    return {
      label: "Payment Pending",
      tone: "pending",
      actions: [],
      canRetryPayment: false,
      canViewInvoice: false,
      canDownloadReceipt: false,
      invoicePath: null,
    };
  }

  if (normalizedStatus === "payment failed") {
    return {
      label: "Payment Failed",
      tone: "failed",
      actions: [],
      canRetryPayment: false,
      canViewInvoice: false,
      canDownloadReceipt: false,
      invoicePath: null,
    };
  }

  if (
    normalizedStatus === "approved" ||
    normalizedStatus === "shipped" ||
    normalizedStatus === "in transit" ||
    normalizedStatus === "delayed" ||
    normalizedStatus === "completed"
  ) {
    return {
      label: "Payment Successful",
      tone: "success",
      actions: [
        {
          key: "view-invoice",
          label: "View Invoice",
          variant: "primary",
          disabled: !invoiceId,
        },
        {
          key: "download-receipt",
          label: "Download Receipt",
          variant: "success",
          disabled: true,
        },
      ],
      canRetryPayment: false,
      canViewInvoice: Boolean(invoiceId),
      canDownloadReceipt: false,
      invoicePath: invoiceId ? `/InvoiceHome/${invoiceId}` : null,
    };
  }

  return {
    label: String(order?.status ?? "").trim() || "Pending",
    tone: "neutral",
    actions: [],
    canRetryPayment: false,
    canViewInvoice: false,
    canDownloadReceipt: false,
    invoicePath: null,
  };
};

const ContactLine = ({ label, value, tagText }) => {
  if (!value) {
    return null;
  }

  return (
    <div className="view-new-direct-order__detail-row">
      <Text variant="bodyText" color="lightText">
        {label}
      </Text>
      <div className="view-new-direct-order__detail-value-group">
        <div className="view-new-direct-order__contact-inline">
          <ProfileProviderIcon
            size={16}
            color="currentColor"
            className="view-new-direct-order__contact-icon"
          />
          <Text variant="bodyText" color="lightText">
            {value}
          </Text>
          {tagText ? (
            <Tags
              tagBackground="blueBackground"
              textColor="primaryColor"
              tagText={tagText}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value, strong = false }) => (
  <div className="view-new-direct-order__detail-row">
    <Text variant="bodyText" color="lightText">
      {label}
    </Text>
    <Text
      variant="bodyText"
      className={
        strong
          ? "view-new-direct-order__text-strong"
          : "view-new-direct-order__text-regular"
      }
    >
      {value || "--"}
    </Text>
  </div>
);

const AddressBlock = ({ data }) => (
  <div className="view-new-direct-order__address-block">
    <Text
      variant="bodyText"
      className="view-new-direct-order__text-strong view-new-direct-order__address-title"
    >
      {data?.accountName || "--"}
    </Text>
    {(data?.addressLines || []).map((line) => (
      <Text
        key={line}
        variant="bodyText"
        color="lightText"
        className="view-new-direct-order__address-line"
      >
        {line}
      </Text>
    ))}
    <ContactLine
      label="Contact"
      value={data?.contactName}
      tagText={data?.contactTag}
    />
    <DetailRow label="Phone" value={data?.phone} />
    <DetailRow label="Fax" value={data?.fax} />
  </div>
);

const SummaryAmount = ({ value, strong = false }) => {
  if (value == null || isNaN(Number(value))) {
    return (
      <span
        className={
          strong
            ? "view-new-direct-order__summary-amount view-new-direct-order__summary-amount--strong"
            : "view-new-direct-order__summary-amount"
        }
      >
        TBD
      </span>
    );
  }
  const [whole, decimal] = formatCurrency(value).split(".");

  return (
    <span
      className={
        strong
          ? "view-new-direct-order__summary-amount view-new-direct-order__summary-amount--strong"
          : "view-new-direct-order__summary-amount"
      }
    >
      ${whole}
      <span className="view-new-direct-order__summary-amount-decimal">.{decimal}</span>
    </span>
  );
};

const PaymentStateCard = ({ statusCard, onAction, isSubmitting }) => {
  const isSuccessState = statusCard?.tone === "success";
  const isFailedState = statusCard?.tone === "failed";

  return (
    <div
      className={`view-new-direct-order__payment-card view-new-direct-order__payment-card--${statusCard?.tone || "neutral"
        }`}
    >
      <div className="view-new-direct-order__payment-copy">
        <Text variant="bodyText" color="darkText">
          Order Status
        </Text>
        <div className="view-new-direct-order__payment-status-row">
          {isFailedState ? (
            <span className="view-new-direct-order__payment-icon view-new-direct-order__payment-icon--failed">
              !
            </span>
          ) : null}
          {isSuccessState ? (
            <span className="view-new-direct-order__payment-icon view-new-direct-order__payment-icon--success">
              <TickIcon width={12} height={9} />
            </span>
          ) : null}
          <Text
            variant="bodyText"
            className={`view-new-direct-order__payment-label view-new-direct-order__payment-label--${statusCard?.tone || "neutral"
              }`}
          >
            {statusCard?.label || "--"}
          </Text>
        </div>
        {statusCard?.helperText ? (
          <Text variant="bodyText" color="lightText">
            {statusCard.helperText}
          </Text>
        ) : null}
      </div>

      <div className="view-new-direct-order__payment-actions">
        {(statusCard?.actions || []).map((action) => {
          const isDisabled = action.disabled || isSubmitting;
          const tooltipTitle =
            action.key === "view-invoice" && action.disabled
              ? "Invoice is not yet generated"
              : action.key === "track-order" && action.disabled
                ? "Tracking is not yet added"
                : null;

          const actionButton = (
            <Button
              key={action.key}
              type="button"
              variant="table"
              subVariant={action.variant}
              disabled={isDisabled}
              onClick={() => onAction(action.key)}
            >
              {action.label}
            </Button>
          );

          if (!tooltipTitle) {
            return actionButton;
          }

          return (
            <Tooltip key={action.key} title={tooltipTitle}>
              <span>{actionButton}</span>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

const ViewNewDirectOrder = ({ publicMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const [messageApi, contextHolder] = message.useMessage();
  const [viewData, setViewData] = useState(null);
  const [livePaymentStatus, setLivePaymentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [publicAccessToken, setPublicAccessToken] = useState("");
  const [publicTokenReady, setPublicTokenReady] = useState(!publicMode);
  const pollingTimerRef = useRef(null);
  const pollingStartedAtRef = useRef(null);
  const pollingRequestInFlightRef = useRef(false);
  const checkoutReturnRef = useRef({
    paymentStatus: null,
    attemptId: null,
    sessionId: null,
  });
  const lastResolvedStateRef = useRef(null);
  const didConsumeLaunchIntentRef = useRef(false);

  const fullName = useMemo(() => buildUserFullName(user), [user]);
  const orderId = useMemo(() => Number(id), [id]);

  useEffect(() => {
    if (!publicMode) {
      return;
    }
    setPublicAccessToken(captureOpenOrderAccessToken());
    setPublicTokenReady(true);
  }, [publicMode]);

  const clearPaymentPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      window.clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    pollingStartedAtRef.current = null;
  }, []);

  const fetchOrderView = useCallback(async ({ background = false } = {}) => {
    if (publicMode && !publicTokenReady) {
      return;
    }

    if (publicMode && !publicAccessToken) {
      setViewData(null);
      setLivePaymentStatus(null);
      setIsLoading(false);
      if (!background) {
        messageApi.error("This Open Order access link is invalid or expired.");
      }
      return;
    }

    if (!publicMode && (!Number.isFinite(orderId) || orderId <= 0)) {
      setViewData(null);
      setLivePaymentStatus(null);
      setIsLoading(false);
      return;
    }

    if (!background) {
      setIsLoading(true);
    }
    try {
      const response = publicMode
        ? await new PublicAPIService().getOpenOrderView(publicAccessToken)
        : await new APIService().getDataFromBackend(
            `newdirectorder/view?ID=${orderId}`,
          );
      const nextViewData = response?.data?.data ?? null;
      setViewData(nextViewData);
      setLivePaymentStatus(nextViewData?.paymentStatus ?? null);
    } catch (error) {
      console.error("[ViewNewDirectOrder] Failed to fetch order", error);
      if (!background) {
        setViewData(null);
        setLivePaymentStatus(null);
        messageApi.error(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to load direct order details.",
        );
      }
    } finally {
      if (!background) {
        setIsLoading(false);
      }
    }
  }, [messageApi, orderId, publicAccessToken, publicMode, publicTokenReady]);

  useEffect(() => {
    if (publicMode || !publicTokenReady) {
      return;
    }
    fetchOrderView();
  }, [fetchOrderView, publicMode, publicTokenReady]);

  useEffect(() => {
    if (!publicMode || !publicTokenReady) {
      return;
    }
    fetchOrderView();
  }, [fetchOrderView, publicMode, publicTokenReady]);

  useEffect(() => {
    if (publicMode) {
      return;
    }
    if (didConsumeLaunchIntentRef.current) {
      return;
    }

    const checkoutUrl = consumeCheckoutLaunchIntent(orderId);
    if (!checkoutUrl) {
      didConsumeLaunchIntentRef.current = true;
      return;
    }

    didConsumeLaunchIntentRef.current = true;
    openCheckoutInSameTab(checkoutUrl);
  }, [orderId, publicMode]);

  const fetchCheckoutSessionStatus = useCallback(
    async ({ attemptId, sessionId, forceRefresh = false }) => {
      const searchParams = new URLSearchParams();
      if (attemptId) {
        searchParams.set("attemptId", attemptId);
      }
      if (sessionId) {
        searchParams.set("sessionId", sessionId);
      }
      if (forceRefresh) {
        searchParams.set("forceRefresh", "true");
      }

      const apiService = new APIService();
      const response = await apiService.getDataFromBackend(
        `payments/checkout-session-status?${searchParams.toString()}`,
      );
      return response?.data?.data?.paymentStatus ?? null;
    },
    [],
  );

  useEffect(() => {
    if (publicMode) {
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const paymentStatus = searchParams.get("payment");
    const attemptId = searchParams.get("attempt_id");
    const sessionId = searchParams.get("session_id");

    if (!paymentStatus && !attemptId && !sessionId) {
      return;
    }

    checkoutReturnRef.current = {
      paymentStatus,
      attemptId,
      sessionId,
    };

    if (paymentStatus === "cancelled") {
      messageApi.open({
        key: "payment-status-sync",
        type: "warning",
        content: "Payment was cancelled. You can continue payment from this order.",
        duration: 3,
      });
    } else if (paymentStatus === "success") {
      messageApi.open({
        key: "payment-status-sync",
        type: "loading",
        content: "Payment received. Updating order status...",
        duration: 2,
      });
    }

    navigate(location.pathname, { replace: true });
  }, [
    location.pathname,
    location.search,
    messageApi,
    navigate,
    publicMode,
  ]);

  const effectivePaymentStatus = livePaymentStatus ?? viewData?.paymentStatus ?? null;
  const paymentAttempt = viewData?.paymentAttempt ?? null;
  const pollingAttemptId =
    effectivePaymentStatus?.attemptId ??
    paymentAttempt?.attemptId ??
    checkoutReturnRef.current.attemptId;
  const pollingSessionId =
    effectivePaymentStatus?.sessionId ??
    paymentAttempt?.sessionId ??
    checkoutReturnRef.current.sessionId;
  const shouldPollPaymentStatus =
    effectivePaymentStatus?.shouldPoll === true ||
    paymentAttempt?.shouldPoll === true ||
    (
      viewData?.order?.isPrepaidOrder === true &&
      Boolean(pollingAttemptId || pollingSessionId) &&
      effectivePaymentStatus?.isTerminal !== true
    );

  useEffect(() => {
    if (publicMode) {
      return undefined;
    }
    lastResolvedStateRef.current = effectivePaymentStatus?.resolvedState ?? null;
  }, [effectivePaymentStatus?.resolvedState, publicMode]);

  useEffect(() => {
    if (publicMode) {
      return undefined;
    }
    if (!pollingAttemptId && !pollingSessionId) {
      clearPaymentPolling();
      return undefined;
    }

    if (!shouldPollPaymentStatus || effectivePaymentStatus?.isTerminal === true) {
      clearPaymentPolling();
      return undefined;
    }

    let cancelled = false;

    const scheduleNextPoll = (delayMs) => {
      if (cancelled) {
        return;
      }
      pollingTimerRef.current = window.setTimeout(runPollCycle, delayMs);
    };

    const runPollCycle = async () => {
      if (cancelled || pollingRequestInFlightRef.current) {
        return;
      }

      pollingRequestInFlightRef.current = true;

      try {
        const nextStatus = await fetchCheckoutSessionStatus({
          attemptId: pollingAttemptId,
          sessionId: pollingSessionId,
          forceRefresh: true,
        });

        if (cancelled || !nextStatus) {
          return;
        }

        const previousResolvedState = lastResolvedStateRef.current;
        const nextResolvedState = nextStatus?.resolvedState ?? null;

        setLivePaymentStatus(nextStatus);
        lastResolvedStateRef.current = nextResolvedState;

        if (nextStatus?.isTerminal) {
          clearPaymentPolling();
          await fetchOrderView({ background: true });
          return;
        }

        if (previousResolvedState && previousResolvedState !== nextResolvedState) {
          await fetchOrderView({ background: true });
        }
      } catch (error) {
        console.error("[ViewNewDirectOrder] Failed to poll checkout status", error);
      } finally {
        pollingRequestInFlightRef.current = false;
      }

      if (cancelled) {
        return;
      }

      if (!pollingStartedAtRef.current) {
        pollingStartedAtRef.current = Date.now();
      }

      const elapsedMs = Date.now() - pollingStartedAtRef.current;
      scheduleNextPoll(elapsedMs < 60_000 ? 2_000 : 10_000);
    };

    if (!pollingStartedAtRef.current) {
      pollingStartedAtRef.current = Date.now();
    }

    runPollCycle();

    return () => {
      cancelled = true;
      if (pollingTimerRef.current) {
        window.clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [
    clearPaymentPolling,
    effectivePaymentStatus?.isTerminal,
    fetchCheckoutSessionStatus,
    fetchOrderView,
    paymentAttempt?.attemptId,
    paymentAttempt?.sessionId,
    paymentAttempt?.shouldPoll,
    pollingAttemptId,
    pollingSessionId,
    shouldPollPaymentStatus,
    viewData?.order?.isPrepaidOrder,
    publicMode,
  ]);

  useEffect(() => {
    if (publicMode) {
      return undefined;
    }
    if (!shouldPollPaymentStatus || (!pollingAttemptId && !pollingSessionId)) {
      return undefined;
    }

    const handleFocusRefresh = async () => {
      if (document.visibilityState !== "visible" || pollingRequestInFlightRef.current) {
        return;
      }

      try {
        const nextStatus = await fetchCheckoutSessionStatus({
          attemptId: pollingAttemptId,
          sessionId: pollingSessionId,
          forceRefresh: true,
        });

        if (!nextStatus) {
          return;
        }

        setLivePaymentStatus(nextStatus);
        lastResolvedStateRef.current = nextStatus?.resolvedState ?? null;

        if (nextStatus?.isTerminal) {
          clearPaymentPolling();
        }

        await fetchOrderView({ background: true });
      } catch (error) {
        console.error("[ViewNewDirectOrder] Failed to refresh checkout status on focus", error);
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [
    clearPaymentPolling,
    fetchCheckoutSessionStatus,
    fetchOrderView,
    pollingAttemptId,
    pollingSessionId,
    shouldPollPaymentStatus,
    publicMode,
  ]);

  useEffect(() => {
    if (!publicMode || !effectivePaymentStatus?.shouldPoll) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      fetchOrderView({ background: true });
    }, 10000);
    return () => window.clearInterval(timer);
  }, [effectivePaymentStatus?.shouldPoll, fetchOrderView, publicMode]);

  const handleApprove = async () => {
    if (publicMode) {
      return;
    }
    if (!viewData?.permissions?.canApprove || !orderId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const apiService = new APIService();
      await apiService.updateDataToBackend(
        {
          ID: orderId,
          Status: "Approved",
          ApprovedBy: fullName,
          ApprovedDate: new Date().toISOString(),
        },
        "updatedirectorder?type=approve",
      );
      messageApi.success("Order approved successfully.");
      await fetchOrderView();
    } catch (error) {
      console.error("[ViewNewDirectOrder] Failed to approve order", error);
      messageApi.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to approve order.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (publicMode) {
      return;
    }
    if (!viewData?.permissions?.canReject || !orderId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const apiService = new APIService();
      await apiService.updateDataToBackend(
        {
          ID: orderId,
          Status: "Rejected",
          RejectedBy: fullName,
          RejectedDate: new Date().toISOString(),
          RejectRemarks: rejectRemarks.trim(),
        },
        "updatedirectorder?type=reject",
      );
      messageApi.success("Order rejected successfully.");
      setRejectRemarks("");
      setIsRejectModalOpen(false);
      await fetchOrderView();
    } catch (error) {
      console.error("[ViewNewDirectOrder] Failed to reject order", error);
      messageApi.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to reject order.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    if (publicMode) {
      return;
    }
    if (!orderId) {
      return;
    }

    navigate(`/DirectOrderLegacy/${orderId}`);
  };

  const handleViewInvoice = ({ requireCompleted = false } = {}) => {
    if (publicMode) {
      return;
    }
    const invoiceId = viewData?.summary?.invoiceId;
    const invoiceCompleted = viewData?.summary?.invoiceCompleted === true;
    const invoicePath =
      viewData?.paymentStatus?.invoicePath || (invoiceId ? `/InvoiceHome/${invoiceId}` : null);

    if (!invoicePath || (requireCompleted && !invoiceCompleted)) {
      return;
    }

    navigate(invoicePath);
  };

  const handleTrackOrder = () => {
    if (publicMode) {
      return;
    }
    const trackingSearchUrl = buildTrackingSearchUrl(viewData?.order?.trackingNumber);

    if (!trackingSearchUrl) {
      messageApi.warning("Tracking is not yet added.");
      return;
    }

    const trackingWindow = window.open(
      trackingSearchUrl,
      "_blank",
      "noopener,noreferrer",
    );

    if (trackingWindow) {
      trackingWindow.opener = null;
    }
  };

  const handleRetryPayment = async (actionLabel = "Retry payment") => {
    if (publicMode) {
      return;
    }
    if (!orderId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const apiService = new APIService();
      const response = await apiService.addDataToBackend(
        { directOrderId: orderId },
        "payments/direct-order/retry",
      );
      const retryData = response?.data?.data ?? {};
      const checkoutUrl = retryData?.checkoutUrl;
      const nextPaymentStatus = retryData?.paymentStatus ?? null;

      if (nextPaymentStatus) {
        setLivePaymentStatus(nextPaymentStatus);
      }

      if (!checkoutUrl) {
        throw new Error(`${actionLabel} checkout URL was not returned.`);
      }

      messageApi.success("Redirecting to secure checkout...");
      openCheckoutInSameTab(checkoutUrl);
    } catch (error) {
      console.error("[ViewNewDirectOrder] Failed to retry payment", error);
      messageApi.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        `Failed to ${actionLabel.toLowerCase()}.`,
      );
      await fetchOrderView({ background: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusCardAction = (actionKey) => {
    if (publicMode) {
      return;
    }
    const isApprovedNonPrepaidBanner =
      String(viewData?.order?.status ?? "").trim().toLowerCase() === "approved" &&
      viewData?.order?.isPrepaidOrder !== true;

    switch (actionKey) {
      case "edit-order":
        handleEdit();
        return;
      case "view-invoice":
        handleViewInvoice({ requireCompleted: isApprovedNonPrepaidBanner });
        return;
      case "download-receipt":
        handleDownloadReceipt();
        return;
      case "track-order":
        handleTrackOrder();
        return;
      case "retry-payment":
        handleRetryPayment("Retry payment");
        return;
      case "continue-payment":
        handleRetryPayment("Continue payment");
        return;
      default:
    }
  };

  const handleDownloadReceipt = async () => {
    if (publicMode) {
      return;
    }
    if (!orderId) {
      return;
    }

    setIsSubmitting(true);
    try {
      const apiService = new APIService();
      const response = await apiService.getDataFromBackend(
        `payments/direct-order/receipt?directOrderId=${orderId}`,
      );
      const receiptUrl = response?.data?.data?.receiptUrl;

      if (!receiptUrl) {
        throw new Error("Receipt URL is not available.");
      }

      window.open(receiptUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("[ViewNewDirectOrder] Failed to download receipt", error);
      messageApi.error(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to download receipt.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="view-new-direct-order__loading">
        {contextHolder}
        <Spin size="large" />
      </div>
    );
  }

  if (!viewData) {
    return (
      <div className={`view-new-direct-order__state ${publicMode ? "view-new-direct-order__public-state" : ""}`}>
        {contextHolder}
        <Text variant="h1">{publicMode ? "Open Order not available" : "Direct order not available"}</Text>
        <Text variant="bodyText" color="lightText">
          {publicMode
            ? "This Open Order access link is invalid or expired."
            : "The order could not be loaded or is outside your access scope."}
        </Text>
        {!publicMode ? (
          <Button variant="table" subVariant="primary" onClick={fetchOrderView}>
            Retry
          </Button>
        ) : null}
      </div>
    );
  }

  const { details, header, order, permissions, summary } = viewData;
  const derivedStatusCard = deriveStatusCard({
    order,
    paymentStatus: effectivePaymentStatus,
    summary,
  });
  const canShowEditAction =
    !publicMode && permissions?.canEdit === true && hasPermission(user, "order.edit");
  const baseStatusCard = publicMode
    ? {
        ...derivedStatusCard,
        actions: [],
        invoicePath: null,
        canRetryPayment: false,
        canContinuePayment: false,
        canViewInvoice: false,
        canDownloadReceipt: false,
      }
    : derivedStatusCard;
  if (["In Transit", "Delayed"].includes(order?.status)) {
    baseStatusCard.label = order.status;
    baseStatusCard.tone = order.status === "Delayed" ? "warning" : "neutral";
  }
  const resolvedStatusCard = canShowEditAction
    ? {
        ...baseStatusCard,
        actions: [
          {
            key: "edit-order",
            label: "Edit",
            variant: "warning",
            disabled: false,
          },
          ...(baseStatusCard?.actions || []),
        ],
      }
    : baseStatusCard;
  const headerTag = getHeaderTagConfig(
    ["Shipped", "In Transit", "Delayed"].includes(order?.status) ? order.status : effectivePaymentStatus?.statusLabel || order?.status,
    ["Shipped", "In Transit", "Delayed"].includes(order?.status) ? order.status : effectivePaymentStatus?.statusLabel || header?.statusTag,
  );
  const summaryProductLabel =
    summary?.productGroupName ||
    (summary?.items?.length === 1
      ? summary?.items?.[0]?.productName
      : summary?.items?.length
        ? `${summary.items.length} Products`
        : "--");

  return (
    <div className={`view-new-direct-order ${publicMode ? "view-new-direct-order--public" : ""}`}>
      {contextHolder}
      {!publicMode && order.shippingNote && <div className="shipping-note" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", marginBottom: 16 }}><strong>Shipping Note:</strong> {order.shippingNote}</div>}
      {isSubmitting ? (
        <Spin
          fullscreen
          size="large"
          className="view-new-direct-order__fullscreen-spinner"
        />
      ) : null}

      <div className="view-new-direct-order__header">
        <div className="view-new-direct-order__header-left">
          <div className="view-new-direct-order__header-main">
            <Text variant="h1">{header?.poNumber || order?.poNumber || "--"}</Text>
            <Tags
              tagBackground={headerTag.tagBackground}
              textColor={headerTag.textColor}
              tagText={headerTag.text}
            />
          </div>
        </div>
        <div className="view-new-direct-order__header-right">
          <Text variant="h1" color="lightText">
            {header?.accountName || "--"}
          </Text>
        </div>
      </div>

      <div className="view-new-direct-order__layout">
        <div className="view-new-direct-order__content">
          <PaymentStateCard
            statusCard={resolvedStatusCard}
            onAction={handleStatusCardAction}
            isSubmitting={isSubmitting}
          />

          <div className="view-new-direct-order__card view-new-direct-order__card--split">
            <div className="view-new-direct-order__card-column view-new-direct-order__card-column--bordered">
              <Text variant="bodyText" color="lightText">
                {details?.shipTo?.title || "Ship to"}
              </Text>
              <AddressBlock data={details?.shipTo} />
            </div>
            <div className="view-new-direct-order__card-column">
              <Text variant="bodyText" color="lightText">
                {details?.billTo?.title || "Bill to"}
              </Text>
              <AddressBlock data={details?.billTo} />
            </div>
          </div>

          <div className="view-new-direct-order__card">
            <DetailRow
              label={details?.distributor?.title || "Distributor"}
              value={details?.distributor?.value}
              strong
            />
            <ContactLine
              label={details?.distributor?.salesRepLabel || "Sales Rep"}
              value={details?.distributor?.salesRepValue}
            />
          </div>

          {details?.provider?.value ? (
            <div className="view-new-direct-order__card">
              <ContactLine
                label={details?.provider?.title || "Provider’s Name"}
                value={details?.provider?.value}
                tagText={details?.provider?.tag}
              />
            </div>
          ) : null}

          {!publicMode ||
          details?.caseInfo?.caseId ||
          details?.caseInfo?.patientCode ||
          details?.caseInfo?.applicationDate ||
          details?.caseInfo?.applicationNumber ? (
            <div className="view-new-direct-order__card view-new-direct-order__card--split">
              <div className="view-new-direct-order__card-column view-new-direct-order__card-column--bordered">
                <DetailRow label="Case ID" value={details?.caseInfo?.caseId} strong />
                <DetailRow
                  label="Patient Code"
                  value={details?.caseInfo?.patientCode}
                  strong
                />
              </div>
              <div className="view-new-direct-order__card-column">
                <DetailRow
                  label="Application Date"
                  value={formatDate(details?.caseInfo?.applicationDate)}
                />
                <DetailRow
                  label="Application #"
                  value={details?.caseInfo?.applicationNumber}
                />
              </div>
            </div>
          ) : null}
        </div>

        <aside className="view-new-direct-order__sidebar">
          <div className="view-new-direct-order__summary-panel">
            <Text variant="h1">Order Summary</Text>

            <div className="view-new-direct-order__summary-card">
              <DetailRow label="Placed By" value={summary?.placedByName} strong />
              <DetailRow
                label="Manufacturer"
                value={summary?.manufacturerName}
                strong
              />
              <DetailRow label="Product Group" value={summaryProductLabel} />
            </div>

            <div className="view-new-direct-order__summary-card view-new-direct-order__summary-items">
              {(summary?.items || []).length ? (
                (summary?.items || []).map((item) => (
                  <div
                    key={item.id ?? item.key ?? `${item.productCode || "item"}-${item.productName || ""}`}
                    className="view-new-direct-order__summary-item"
                  >
                    <div className="view-new-direct-order__summary-item-left">
                      <Text
                        variant="bodyText"
                        className="view-new-direct-order__text-strong"
                      >
                        {item.productCode || "--"}
                      </Text>
                      <Text variant="bodyText">
                        ${formatCurrency(item.unitPrice)} x {formatQuantity(item.quantity)}
                      </Text>
                    </div>
                    <SummaryAmount value={item.totalPrice} strong />
                  </div>
                ))
              ) : (
                <div className="view-new-direct-order__summary-item view-new-direct-order__summary-item--empty">
                  <Text variant="bodyText" color="lightText">
                    No order items available.
                  </Text>
                </div>
              )}
            </div>

            <div className="view-new-direct-order__summary-card">
              <div className="view-new-direct-order__summary-total-row">
                <Text variant="bodyText" color="lightText">
                  Subtotal
                </Text>
                <SummaryAmount value={summary?.subtotal} />
              </div>
              <div className="view-new-direct-order__summary-total-row">
                <Text variant="bodyText" color="lightText">
                  Shipping Cost
                </Text>
                <SummaryAmount value={summary?.shippingCost} />
              </div>
              {summary?.showProcessingFee ? (
                <div className="view-new-direct-order__summary-total-row">
                  <Text variant="bodyText" color="lightText">
                    {summary?.processingFeeLabel || "Processing Fee"}
                  </Text>
                  <SummaryAmount value={summary?.processingFee} />
                </div>
              ) : null}
              <div className="view-new-direct-order__summary-total-row view-new-direct-order__summary-total-row--grand">
                <Text
                  variant="bodyText"
                  className="view-new-direct-order__text-strong"
                >
                  Grand Total
                </Text>
                <SummaryAmount value={summary?.grandTotal} strong />
              </div>
            </div>
          </div>
        </aside>
      </div>

      <Modal
        title="Reject Order?"
        open={isRejectModalOpen}
        onCancel={() => {
          if (isSubmitting) {
            return;
          }
          setIsRejectModalOpen(false);
        }}
        footer={null}
        centered
      >
        <div className="view-new-direct-order__reject-modal">
          <Text variant="bodyText" color="lightText">
            This action will reject the order and notify the submitter by email.
          </Text>
          <TextArea
            value={rejectRemarks}
            onChange={(event) => setRejectRemarks(event.target.value)}
            autoSize={{ minRows: 4, maxRows: 6 }}
            placeholder="Add rejection remarks here..."
          />
          <div className="view-new-direct-order__reject-actions">
            <Button
              variant="table"
              subVariant="outline"
              disabled={isSubmitting}
              onClick={() => setIsRejectModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="table"
              subVariant="failure"
              disabled={isSubmitting}
              onClick={handleReject}
            >
              Reject with Remarks
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ViewNewDirectOrder;
